import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  createAIAgents,
  resetFormDetails,
  saveFormDetails,
  resetCreateAgent,
  saveSlugName,
} from '../../../redux/slices/CreateAgentSlice';
import { resetDeployAgent, saveTaskId } from '../../../redux/slices/DeployAIAgent';
import { getCsvDataBySlug, updateCsvDataAction, clearSuccessMessage, clearCsvData } from '../../../redux/slices/CSVDataSlice';
import { getStatisticsAgents, clearStatisticInfo } from '../../../redux/slices/StatisticsSlice';
import { FORM_KEYS } from '../../../utills/constant';

export const useInvoiceAgents = () => {
  const [selected, setSelected] = useState("Input");
  const [backDisabled, setBackDisabled] = useState(true);
  const [AgenNamePopUp, setAgenNamePopUp] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [showCreateLoader, setShowCreateLoader] = useState(false);
  const [showDeployLoader, setShowDeployLoader] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [error, setErrors] = useState({
    system_instruction: "",
    output_instruction: ""
  });

  const parentRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const { csvData, updateCsvData, systemInstruction, outputInstruction, processingOption, metadataAnalyser, getCsvLoading } = useSelector((state) => state.CSVDataSlice);
  const {
    formDetails,
    createAgentLoading,
    createAgent,
    slugName,
    createAgentError,
    outputOcrFields,
  } = useSelector((state) => state.CreateAgentSlice);
  const { deployAgentLoading, deployAgentError, deployAgent } = useSelector((state) => state.DeploySlice);
  const { profileDetails } = useSelector((state) => state.ProfileSlice);
  const { statisticInfo, statisticInfoLoading } = useSelector((state) => state.StatisticsSlice);

  const receivedData = location.state;

  console.log("metadataAnalyser", metadataAnalyser)

  // Initialize data when component mounts
  useEffect(() => {
    if (receivedData?.slug === "document-classifier") {
      setSelected("Input");
    } else if (receivedData?.steps) {
      setSelected(receivedData?.steps);
      // Clear any existing data first to prevent showing stale data
      dispatch(clearCsvData());
      dispatch(resetFormDetails());
      // Fetch both CSV data and agent details
      dispatch(getCsvDataBySlug(receivedData?.slug_id));
      dispatch(getStatisticsAgents(receivedData?.slug_id));
    } else {
      // Create new agent mode - clear any old agent data
      dispatch(clearStatisticInfo());
    }
  }, []);

  // Update back button state whenever `selected` changes
  useEffect(() => {
    if (selected === "Input") {
      setBackDisabled(true);
    } else {
      setBackDisabled(false);
    }
  }, [selected]);

  // Handle CSV data updates
  useEffect(() => {
    if (updateCsvData) {
      setSnackbar({
        open: true,
        message: updateCsvData,
        severity: "success",
      });
      dispatch(getCsvDataBySlug(receivedData?.slug_id));
      dispatch(clearSuccessMessage());
    }
  }, [updateCsvData]);

  // Prefill system instruction when editing
  useEffect(() => {
    if (systemInstruction && receivedData?.steps) {
      dispatch(
        saveFormDetails({
          key: FORM_KEYS.SYSTEM_INSTRUCTION,
          value: systemInstruction,
        })
      );
    } else if (!receivedData?.steps) {
      dispatch(
        saveFormDetails({
          key: FORM_KEYS.SYSTEM_INSTRUCTION,
          value: "",
        })
      );
    }
  }, [systemInstruction, receivedData?.steps, dispatch]);

  // Prefill output instruction when editing
  useEffect(() => {
    if (outputInstruction && receivedData?.steps) {
      dispatch(
        saveFormDetails({
          key: FORM_KEYS.OUTPUT_INSTRUCTION,
          value: outputInstruction,
        })
      );
    } else if (!receivedData?.steps) {
      dispatch(
        saveFormDetails({
          key: FORM_KEYS.OUTPUT_INSTRUCTION,
          value: "",
        })
      );
    }
  }, [outputInstruction, receivedData?.steps, dispatch]);

  // Prefill processing option when editing
  useEffect(() => {
    if (processingOption && receivedData?.steps) {
      dispatch(
        saveFormDetails({
          key: FORM_KEYS.PROCESSING_OPTIONS,
          value: processingOption,
        })
      );
    } else if (!receivedData?.steps) {
      dispatch(
        saveFormDetails({
          key: FORM_KEYS.PROCESSING_OPTIONS,
          value: "",
        })
      );
    }
  }, [processingOption, receivedData?.steps, dispatch]);

  // Prefill metadata checkbox value when editing
  useEffect(() => {
    if (receivedData?.steps) {
      dispatch(
        saveFormDetails({
          key: FORM_KEYS.META_DATA_ANALYSER,
          value: metadataAnalyser ?? false,
        })
      );
    } else {
      // New agent mode → reset
      dispatch(
        saveFormDetails({
          key: FORM_KEYS.META_DATA_ANALYSER,
          value: false,
        })
      );
    }
  }, [metadataAnalyser, receivedData?.steps, dispatch]);

  // Initialize form details
  useEffect(() => {
    dispatch(
      saveFormDetails({
        key: FORM_KEYS.ORGANIZATION_ID,
        value: profileDetails?.organization ?? 1,
      })
    );
    dispatch(saveFormDetails({ key: FORM_KEYS.IS_ACTIVE, value: true }));
    dispatch(
      saveFormDetails({ key: FORM_KEYS.IS_AVAILABLE_TO_ALL_USERS, value: true })
    );
    dispatch(
      saveFormDetails({
        key: FORM_KEYS.SYSTEM_AI_AGENT_SLUG,
        value: receivedData?.slug,
      })
    );

    if (!receivedData?.steps) {
      dispatch(
        saveFormDetails({
          key: FORM_KEYS.SYSTEM_INSTRUCTION,
          value: "",
        })
      );
      dispatch(
        saveFormDetails({
          key: FORM_KEYS.OUTPUT_INSTRUCTION,
          value: "",
        })
      );
      dispatch(
        saveFormDetails({
          key: FORM_KEYS.PROCESSING_OPTIONS,
          value: "",
        })
      );
    }
  }, [receivedData, profileDetails]);

  // Handle CreateAgent response
  useEffect(() => {
    if (!createAgentLoading && createAgent) {
      setShowCreateLoader(false);
      setDialogOpen(false);
      setSnackbar({
        open: true,
        message: "Agent created successfully!",
        severity: "success",
      });
      dispatch(saveSlugName(createAgent?.data?.slug));
      setSelected("Review");
      dispatch(resetCreateAgent());
    }

    if (!createAgentLoading && createAgentError) {
      setShowCreateLoader(false);
      setSnackbar({
        open: true,
        message: "Failed to create agent. Please try again.",
        severity: "error",
      });
    }
  }, [createAgentLoading, createAgent, createAgentError]);

  // Handle deploy AI agent response
  useEffect(() => {
    if (!deployAgentLoading && deployAgent) {
      setShowDeployLoader(false);
      setDialogOpen(false);
      setSnackbar({
        open: true,
        message: "Agent deployed successfully!",
        severity: "success",
      });
      dispatch(saveTaskId(deployAgent?.data?.task_id));
      dispatch(resetCreateAgent());
      dispatch(resetDeployAgent());
      dispatch(resetFormDetails());
      setTimeout(() => {
        navigate('/LoadMyAgent');
      }, 2000);
      // setTimeout(() => {
      //   navigate("/NewAgentViewerScreen", { state: { slugName, needsRefresh: false } });
      // }, 2000);
    }

    if (!deployAgentLoading && deployAgentError) {
      setShowDeployLoader(false);
      setSnackbar({
        open: true,
        message: "Failed to create agent. Please try again.",
        severity: "error",
      });
      dispatch(resetDeployAgent());
    }
  }, [deployAgentLoading, deployAgent, deployAgentError]);

  const getCsvHeaders = () => {
    const csvHeaders = {
      Unique: "Unique identifier for the record",
      Checksum: "Checksum value for data integrity",
      Keys: "Output key used for reference",
      Description: "Details about the key",
      Format: "Expected format (e.g., string, integer, date)",
      Fields: '',
      Fields_Description: ''
    };
    return Object.keys(csvHeaders);
  };

  const handleUpdateDetails = () => {
    const data = {
      slug: receivedData?.slug_id,
      params: csvData,
      systemInstruction: formDetails?.system_instruction,
      outputInstruction: formDetails?.output_instruction,
      processingOption: formDetails?.processing_options,
      metadataAnalyser: formDetails?.meta_data_analyser
    };
    dispatch(updateCsvDataAction(data));
  };

  const handleCreateAgent = () => {
    setAgenNamePopUp(false);
    setShowCreateLoader(true); // Show the beautiful loader
    let updatedFormDetails = {
      ...formDetails,
      [FORM_KEYS.OCR_FIELDS]: outputOcrFields,
    };
    updatedFormDetails['csv_headers'] = [];

    if (updatedFormDetails.system_ai_agent_slug === 'moad-so' || updatedFormDetails.system_ai_agent_slug === 'extract-iq') {
      updatedFormDetails['csv_headers'] = getCsvHeaders();
      updatedFormDetails[FORM_KEYS.OCR_FIELDS] = csvData;
    }

    dispatch(createAIAgents(updatedFormDetails));
  };

  const handleButtonClick = (button) => {
    setSelected(button);
  };

  const handleDialogOpen = () => {
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const validateAndUpdate = () => {
    const newErrors = {
      system_instruction: formDetails?.system_instruction?.trim() === "" || !formDetails?.system_instruction ? "System instruction is required" : "",
      output_instruction: formDetails?.output_instruction?.trim() === "" || !formDetails?.output_instruction ? "Output instruction is required" : "",
    };

    setErrors(newErrors);

    const hasErrors = Object.values(newErrors).some((error) => error !== "");
    if (!hasErrors) {
      handleUpdateDetails();
    } else {
      setSnackbar({
        open: true,
        message: "Please fill in all required fields before updating",
        severity: "error",
      });
    }
  };

  const validateAndProceed = () => {
    if (selected === 'Fields') {
      if (receivedData?.steps) {
        handleUpdateDetails();
      } else {
        const newErrors = {
          system_instruction: formDetails?.system_instruction?.trim() === "" || !formDetails?.system_instruction ? "System instruction is required" : "",
          output_instruction: formDetails?.output_instruction?.trim() === "" || !formDetails?.output_instruction ? "Output instruction is required" : "",
        };

        setErrors(newErrors);

        const hasErrors = Object.values(newErrors).some((error) => error !== "");
        if (!hasErrors) {
          const currentIndex = buttons.findIndex((btn) => btn.value === selected);
          const nextIndex = (currentIndex + 1) % buttons.length;
          handleButtonClick(buttons[nextIndex].value);
        }
      }
    } else if (selected === 'Output') {
      // Check if CSV data exists before allowing to proceed to Review
      if (!csvData || csvData.length === 0) {
        setSnackbar({
          open: true,
          message: "Please upload CSV data in the Fields tab before proceeding to Review",
          severity: "error",
        });
        return;
      }

      const currentIndex = buttons.findIndex((btn) => btn.value === selected);
      const nextIndex = (currentIndex + 1) % buttons.length;
      handleButtonClick(buttons[nextIndex].value);
    } else {
      const currentIndex = buttons.findIndex((btn) => btn.value === selected);
      const nextIndex = (currentIndex + 1) % buttons.length;
      handleButtonClick(buttons[nextIndex].value);
    }
  };

  const handleBackButton = () => {
    const currentIndex = buttons.findIndex((btn) => btn.value === selected);
    if (currentIndex > 0) {
      const nextIndex = (currentIndex - 1) % buttons.length;
      handleButtonClick(buttons[nextIndex].value);
    }
  };

  const handleRunClick = () => {
    // Check if CSV data exists
    if (!csvData || csvData.length === 0) {
      setSnackbar({
        open: true,
        message: "Please upload CSV data first before running the agent",
        severity: "error",
      });
      return;
    }

    setDialogOpen(true); // Show DeployAIBot popup first
  };

  const buttons = receivedData?.slug === "document-classifier" ? [] :
    receivedData?.steps ? [
      { label: "Fields", value: "Fields" },
    ] : [
      { label: "Input", value: "Input" },
      { label: "Fields", value: "Fields" },
      { label: "Output", value: "Output" },
      { label: "Review", value: "Review" },
    ];

  return {
    // State
    selected,
    AgenNamePopUp,
    dialogOpen,
    showCreateLoader,
    showDeployLoader,
    snackbar,
    error,
    parentRef,
    receivedData,
    backDisabled,

    // Redux state
    csvData,
    formDetails,
    createAgentLoading,
    createAgent,
    slugName,
    createAgentError,
    outputOcrFields,
    deployAgentLoading,
    deployAgentError,
    deployAgent,
    profileDetails,
    statisticInfo,
    statisticInfoLoading,

    // Actions
    setAgenNamePopUp,
    setShowCreateLoader,
    setShowDeployLoader,
    setErrors,
    handleUpdateDetails,
    handleCreateAgent,
    handleButtonClick,
    handleDialogOpen,
    handleDialogClose,
    handleCloseSnackbar,
    validateAndUpdate,
    validateAndProceed,
    handleBackButton,
    handleRunClick,

    // Computed values
    buttons,
    getCsvLoading,
  };
};
